/**
 * FAQ.
 *
 * The five questions and answers in design/Homepage.dc.html lines 394-422 were
 * REJECTED by the client as factually wrong. Do not restore them from the
 * reference — this is a deliberate override of the "reference is the copy source
 * of truth" rule. See CLAUDE.md, "Approved deviations from the reference".
 *
 * These six are the client's replacements, supplied 2026-08-19, verbatim. The
 * eyebrow and heading were never rejected and are unchanged.
 *
 * SIX, NOT FIVE. The design has five slots. The client supplied six and put the
 * "AI-native" question first, ahead of the original five. The section is a
 * simple list so the extra item costs nothing structurally.
 *
 * ONE TRANSMISSION FIX. The first answer arrived as "...two specific things.We
 * use AI in the work" — no space after the full stop. Split into two paragraphs
 * at that point rather than joined with a space, because every other answer here
 * opens with exactly that shape: a short direct line, then the explanation.
 * Nothing else was touched.
 *
 * TWO THINGS IN THIS COPY CONTRADICT THE REST OF THE SITE. Both shipped as
 * written because this is client copy, but neither is a detail to leave buried:
 *
 * 1. Q4 says synthetic data "keeps us outside your PHI scope entirely". That
 *    absolute was already walked back on /industries/healthcare, which now reads
 *    "staying outside your PHI scope by default" — because legacy case study #7
 *    is fourteen months of HIPAA compliance and patient-data security testing
 *    that the client confirmed operated INSIDE PHI scope. See CLAUDE.md,
 *    "Corrections the legacy case-study export forced". The homepage now states
 *    the absolute the industry page deliberately stopped stating.
 *
 * 2. Q5 says "Every service page carries a starting-from figure". No service
 *    page carries one. Three of the four render a "Starting from" heading with
 *    the price recorded as an unfilled gap ("Starting-from price and what sets
 *    it"); /services/ai-driven-validation has no price block at all. A reader
 *    who follows this sentence finds nothing.
 */

export const faqSection = {
  eyebrow: "FAQ",
  heading: "Questions technical leaders ask us first.",
} as const;

/** `answer` is paragraphs. The client's copy is written in them. */
export type FaqItem = { question: string; answer: string[] };

export const faqs: FaqItem[] = [
  {
    question: 'Everyone says "AI-native" now. What does it actually mean here?',
    answer: [
      "Fair scepticism. For us it means two specific things.",
      "We use AI in the work — test design, requirement analysis, defect clustering, release-readiness scoring — because it does those things better than a person reading every ticket. And we test AI products for clients who ship them, which is a different discipline again: non-deterministic outputs, LLM-as-a-judge evaluation, adversarial prompt testing.",
      "What it doesn't mean is that AI replaces judgement. AI test design covers the space you've described faster and more completely than a person can. It won't find the bug nobody thought to look for.",
    ],
  },
  {
    question: "How is this different from a QA outsourcing firm?",
    answer: [
      "An outsourcing firm sells you people by the month, and the longer you need them, the better the arrangement works for them.",
      "Three of our four services are fixed scope, fixed price, with a defined end and a handover. We're paid to change how quality works, not to stay. The fourth runs monthly and rolling, with no minimum term — so if it isn't working, you leave.",
    ],
  },
  {
    question: "We already have a QA team. Where does this fit?",
    answer: [
      "Most of our work is with teams that already exist.",
      "The constraint is rarely the people. It's the test architecture they inherited, the process built around a release cadence you no longer run, or the absence of anyone senior enough to set a standard. We work alongside your team and hand over what we build, because a team that can't maintain the new approach reverts to the old one within two quarters.",
      "If the honest answer after an assessment is that you need one more engineer rather than us, we'll tell you that.",
    ],
  },
  {
    question: "You're a small team. What happens if you're busy?",
    answer: [
      "We're a core team of senior practitioners, extended with specialists we've worked with before when an engagement needs particular depth. We don't keep a bench of juniors to place.",
      "That means the people you meet are the people who do the work. It also means we take a small number of clients at a time and occasionally can't start when you'd like — which we'll tell you on the first call rather than stretch and under-deliver.",
    ],
  },
  {
    question: "Do you need access to our production data?",
    answer: [
      "Usually not, and we'd rather not.",
      "We work with synthetic data built to match your real distributions — including the edge cases your production set happens not to contain. In healthcare that keeps us outside your PHI scope entirely, so there's no Business Associate Agreement to negotiate before anything starts. In financial services it means no PII leaves your environment.",
      "Where an engagement genuinely needs production access, we'll say so up front and work to your security requirements rather than around them.",
    ],
  },
  {
    question: "What does an engagement cost, and how is it structured?",
    answer: [
      "Three of our four services are fixed scope, fixed price, four to six weeks. You know the cost before it starts and there's no open-ended engagement to exit.",
      "Intelligent Quality Operations runs as a monthly retainer, rolling, no minimum term.",
      "Price depends on the size of your estate and how much of it is in scope. Every service page carries a starting-from figure so you can size it before you talk to us.",
    ],
  },
];
